import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Target, Award, Globe, TrendingUp, Shield, 
  CheckCircle, ArrowRight, Star, Clock, Zap, Building2,
  Activity, BarChart3, Settings, Eye, Calendar, MapPin,
  ChevronDown, ChevronUp, Mail, Phone, MapPin as Location
} from 'lucide-react';
import Layout from '../../components/layout/Layout';
import Footer from '../../components/layout/Footer';
import { useTheme } from '../../components/ThemeContext';

const AboutPage = () => {
  const { isDarkMode } = useTheme();
  // FAQ state
  const [openFAQ, setOpenFAQ] = useState(null);

  // FAQ data - placeholder until you provide content
  const faqData = [
    {
      question: "What services does AxonMetric provide?",
      answer: "AxonMetric provides comprehensive renewable energy analytics, real-time monitoring, predictive maintenance, and AI-driven optimization solutions for solar, wind, and battery storage systems."
    },
    {
      question: "How does your AI-powered analytics work?",
      answer: "Our proprietary machine learning algorithms analyze vast amounts of renewable energy data to predict performance issues, optimize output, and recommend preventive maintenance actions."
    },
    {
      question: "What types of renewable energy assets do you support?",
      answer: "We support solar panels, wind turbines, battery storage systems, and hybrid renewable energy installations across various scales and configurations."
    },
    {
      question: "Do you provide 24/7 monitoring services?",
      answer: "Yes, we offer round-the-clock monitoring and support services to ensure your renewable energy assets operate at peak performance with minimal downtime."
    },
    {
      question: "How can I get started with AxonMetric services?",
      answer: "You can contact our team through the contact form below or reach out directly. We'll schedule a consultation to understand your needs and provide a customized solution."
    }
  ];

  // What We Do content - placeholder until you provide content
  const whatWeDoContent = {
    title: "What We Do",
    subtitle: "Comprehensive Renewable Energy Solutions",
    description: "At AxonMetric, we specialize in providing cutting-edge analytics and optimization solutions for renewable energy systems. Our comprehensive approach helps maximize the performance and reliability of your renewable energy assets.",
    services: [
      {
        icon: BarChart3,
        title: "Advanced Analytics",
        description: "AI-driven insights and predictive analytics for optimal renewable energy performance and efficiency."
      },
      {
        icon: Activity,
        title: "Real-time Monitoring",
        description: "24/7 surveillance and monitoring of renewable energy assets with instant alerts and notifications."
      },
      {
        icon: Settings,
        title: "Predictive Maintenance",
        description: "Proactive maintenance scheduling and recommendations to minimize downtime and maximize uptime."
      },
      {
        icon: Globe,
        title: "Portfolio Management",
        description: "Comprehensive management solutions for diverse renewable energy portfolios and installations."
      }
    ]
  };

  return (
    <Layout header={6}>
      {/* Hero Section */}
      <section className={`relative py-20 lg:py-28 overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120] text-white' : 'bg-gradient-to-br from-white via-gray-50 to-white text-gray-900'}`}>
        {/* Background Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10" style={{background: 'radial-gradient(circle, #1C4D88 0%, transparent 70%)'}}></div>
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-10" style={{background: 'radial-gradient(circle, #1C4D88 0%, transparent 70%)'}}></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border shadow-sm transition-colors ${isDarkMode ? 'border-slate-700 bg-slate-800/80 text-slate-200' : 'border-gray-200 bg-white text-gray-900'}`}
              >
                <Star size={16} style={{color: '#1C4D88'}} fill="#1C4D88" />
                <span className={`text-sm font-extrabold ${isDarkMode ? 'text-slate-200' : 'text-gray-900'}`}>Leading Renewable Energy Analytics</span>
              </motion.div>

              {/* Main Heading */}
              <div className="space-y-6">
                <h1 className={`text-4xl lg:text-6xl font-extrabold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Empowering 
                  <br />
                  <span className="font-extrabold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                    Renewable Energy
                  </span>
                  <br />
                  <span className={`font-normal ${isDarkMode ? 'text-slate-200' : 'text-gray-900'}`}>Through Innovation</span>
                </h1>
                
                <p className={`text-lg font-extrabold leading-relaxed max-w-xl ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>
                  Pioneers in renewable energy analytics, dedicated to maximizing efficiency and reliability 
                  of wind, solar, and battery storage systems through advanced AI-driven insights.
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="text-center lg:text-left"
                >
                  <div className="text-3xl font-bold" style={{color: isDarkMode ? '#3EA3DA' : '#1C4D88'}}>500+</div>
                  <div className={`text-sm font-extrabold mt-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>Projects</div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="text-center lg:text-left"
                >
                  <div className="text-3xl font-bold" style={{color: isDarkMode ? '#3EA3DA' : '#1C4D88'}}>99.9%</div>
                  <div className={`text-sm font-extrabold mt-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>Uptime</div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="text-center lg:text-left"
                >
                  <div className="text-3xl font-bold" style={{color: isDarkMode ? '#3EA3DA' : '#1C4D88'}}>24/7</div>
                  <div className={`text-sm font-extrabold mt-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>Support</div>
                </motion.div>
              </div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="flex flex-wrap gap-4 pt-2"
              >
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 font-extrabold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-white"
                  style={{backgroundColor: '#1C4D88'}}
                >
                  <span>Get Started</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="/service"
                  className={`inline-flex items-center justify-center gap-2 font-extrabold py-3 px-8 rounded-xl border-2 transition-all duration-300 ${isDarkMode ? 'border-cyan-500 text-cyan-400 bg-slate-900/50 hover:bg-slate-800' : 'hover:bg-gray-50 bg-white'}`}
                  style={{borderColor: isDarkMode ? undefined : '#1C4D88', color: isDarkMode ? undefined : '#1C4D88'}}
                >
                  <span>Learn More</span>
                </a>
              </motion.div>
            </motion.div>

            {/* Right Image Grid */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                {/* Large Image - Top Left */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="col-span-2 relative rounded-2xl overflow-hidden shadow-2xl group"
                >
                  <img
                    src="/assets/img/general/solar-energy-7882650_1280.jpg"
                    alt="Solar Energy"
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="flex items-center gap-2">
                      <Zap size={20} />
                      <span className="font-extrabold">Solar Solutions</span>
                    </div>
                  </div>
                </motion.div>

                {/* Small Image - Bottom Left */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="relative rounded-2xl overflow-hidden shadow-xl group"
                >
                  <img
                    src="/assets/img/general/pinwheels-6535595_1280.jpg"
                    alt="Wind Energy"
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-3 text-white">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp size={16} />
                      <span className="font-extrabold text-sm">Wind Power</span>
                    </div>
                  </div>
                </motion.div>

                {/* Small Image - Bottom Right */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="relative rounded-2xl overflow-hidden shadow-xl group"
                >
                  <img
                    src="/assets/img/general/ai-generated-8685407_1280.jpg"
                    alt="Energy Storage"
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-3 text-white">
                    <div className="flex items-center gap-1.5">
                      <Activity size={16} />
                      <span className="font-extrabold text-sm">Analytics</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className={`py-20 transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-white'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className={`text-2xl lg:text-3xl font-extrabold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {whatWeDoContent.subtitle}
            </h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={`text-base font-extrabold max-w-3xl mx-auto ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}
            >
              {whatWeDoContent.description}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whatWeDoContent.services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative h-full"
              >
                <div className={`rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 text-center h-full flex flex-col border ${isDarkMode ? 'bg-[#131D31] border-[#1E2B42] text-white hover:border-cyan-500/50' : 'bg-white border-gray-100 text-gray-900'}`}>
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-105 transition-transform duration-300" style={{backgroundColor: isDarkMode ? 'rgba(0, 132, 207, 0.15)' : '#1C4D8820'}}>
                    <service.icon style={{color: isDarkMode ? '#3EA3DA' : '#1C4D88'}} size={24} />
                  </div>
                  
                  {/* Content */}
                  <h3 className={`text-lg font-extrabold mb-3 transition-colors duration-300 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {service.title}
                  </h3>
                  <p className={`text-sm font-extrabold leading-relaxed transition-colors duration-300 flex-grow ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>
                    {service.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Optimization Section - Streamline Reporting & Optimize Energy Generation */}
      <section className={`relative py-12 lg:py-16 overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'}`}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
            <motion.div
            className={`rounded-3xl p-8 lg:p-12 shadow-2xl relative overflow-hidden transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-[#131D31] border border-slate-700/60 shadow-slate-950/50' 
                : 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
            }`}
            initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
                >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
              {/* Left Column - Content */}
              <div className="space-y-6">
                <div>
                  <div className="inline-flex items-center px-3 py-1 bg-blue-500/20 rounded-full text-sm font-extrabold mb-4 text-blue-300">
                    <Target className="w-4 h-4 mr-2" />
                    Optimization
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-extrabold text-white mb-4 leading-tight">
                    Streamline Reporting & Optimize
                  <br />
                    <span className="text-cyan-400">Energy Generation</span>
                  </h3>
              </div>

                {/* Feature List */}
                <div className="space-y-4">
                  {[
                    {
                      text: "Monitor renewable asset portfolios in real-time",
                      icon: Activity
                    },
                    {
                      text: "Automated workflows for corrective actions",
                      icon: Settings
                    },
                    {
                      text: "Advanced analytics to optimize energy generation",
                      icon: TrendingUp
                    }
                  ].map((feature, index) => (
              <motion.div
                      key={index}
                      className="flex items-start gap-4 group"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
              >
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <feature.icon className="w-6 h-6 text-white" />
                  </div>
                      <div className="flex-1 pt-2">
                        <p className="text-lg text-gray-200 leading-relaxed">{feature.text}</p>
                  </div>
                    </motion.div>
                  ))}
                  </div>
                </div>

              {/* Right Column - Visual Element */}
              <div className="relative">
                <div className="relative bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl p-8 backdrop-blur-sm border border-white/10">
                  {/* Animated grid pattern with images */}
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      '/assets/img/general/photovoltaic-2814504_1280.jpg',
                      '/assets/img/general/ai-generated-8685407_1280.jpg',
                      '/assets/img/general/solar-6816101_1280.jpg',
                      '/assets/img/general/energy-2302001_1280.jpg',
                      '/assets/img/general/photovoltaic-system-2742302_1280.jpg',
                      '/assets/img/general/wind-power-generation-8370867_1280.jpg',
                      '/assets/img/general/pinwheels-6535595_1280.jpg',
                      '/assets/img/general/solar-energy-7882650_1280.jpg',
                      '/assets/img/general/steptodown.com541656.jpg'
                    ].map((imageSrc, i) => (
                      <div
                        key={i}
                        className="aspect-square rounded-lg overflow-hidden border border-white/20 group hover:scale-110 transition-transform duration-300 shadow-lg hover:shadow-xl"
                      >
                    <img 
                          src={imageSrc}
                          alt={`Renewable Energy ${i + 1}`}
                          className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-300"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Floating icon */}
                  <div className="absolute -top-6 -right-6 w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-xl animate-pulse z-10">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Decorative elements */}
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl"></div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"></div>
              </div>
              </div>
            </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={`py-20 transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-[#F2F8FC]'}`} style={{backgroundColor: isDarkMode ? '#0B1120' : '#1C4D8820'}}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className={`text-2xl lg:text-3xl font-extrabold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Got Questions? 
              <span className="font-normal" style={{color: isDarkMode ? '#3EA3DA' : '#1C4D88'}}> We Have Answers</span>
            </h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={`text-base font-bold max-w-2xl mx-auto ${isDarkMode ? 'text-slate-300' : 'text-black'}`}
            >
              Find answers to common questions about our renewable energy solutions and services
            </motion.p>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`rounded-xl shadow-sm border overflow-hidden transition-colors ${isDarkMode ? 'bg-[#131D31] border-[#1E2B42]' : 'bg-white border-gray-100'}`}
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className={`w-full px-6 py-4 text-left flex items-center justify-between transition-colors duration-200 ${isDarkMode ? 'hover:bg-slate-800/60' : 'hover:bg-gray-50'}`}
                >
                  <h3 className={`text-lg font-extrabold pr-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {faq.question}
                  </h3>
                  {openFAQ === index ? (
                    <ChevronUp style={{color: isDarkMode ? '#3EA3DA' : '#1C4D88'}} size={20} />
                  ) : (
                    <ChevronDown style={{color: isDarkMode ? '#3EA3DA' : '#1C4D88'}} size={20} />
                  )}
                </button>
                {openFAQ === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-4"
                  >
                    <p className={`font-medium leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-gray-900'}`}>
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className={`py-20 transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]' : 'bg-white'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className={`text-2xl lg:text-3xl font-extrabold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Get in Touch with 
              <span className="font-normal" style={{color: isDarkMode ? '#3EA3DA' : '#1C4D88'}}> Our Team</span>
            </h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={`text-base font-bold max-w-2xl mx-auto ${isDarkMode ? 'text-slate-300' : 'text-black'}`}
            >
              Ready to optimize your renewable energy operations? Contact our experts today for a personalized consultation.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{backgroundColor: isDarkMode ? 'rgba(0, 132, 207, 0.15)' : '#1C4D8820'}}>
                    <Mail style={{color: isDarkMode ? '#3EA3DA' : '#1C4D88'}} size={20} />
                  </div>
                  <div>
                    <h3 className={`text-lg font-extrabold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Email Us</h3>
                    <a href="mailto:info@axonmetric.com" className={`font-extrabold transition-colors ${isDarkMode ? 'text-slate-300 hover:text-cyan-400' : 'text-gray-900 hover:text-blue-600'}`}>info@axonmetric.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{backgroundColor: isDarkMode ? 'rgba(0, 132, 207, 0.15)' : '#1C4D8820'}}>
                    <Phone style={{color: isDarkMode ? '#3EA3DA' : '#1C4D88'}} size={20} />
                  </div>
                  <div>
                    <h3 className={`text-lg font-extrabold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Call Us</h3>
                    <a href="tel:+917827355185" className={`font-extrabold transition-colors ${isDarkMode ? 'text-slate-300 hover:text-cyan-400' : 'text-gray-900 hover:text-blue-600'}`}>+91-7827355185</a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form Placeholder */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div 
                className={`relative rounded-2xl p-8 h-96 flex items-center justify-center overflow-hidden border ${isDarkMode ? 'border-[#1E2B42]' : 'border-gray-200'}`}
                style={{ backgroundImage: `url('/assets/img/general/solar-6816101_1280.jpg')` }}
              >
                <div className="absolute inset-0" style={{background: 'linear-gradient(to bottom right, #1C4D88B3, #1C4D88B3, #1C4D88B3)'}}></div>
                <div className="text-center text-white relative z-10">
                  <Mail className="w-12 h-12 mb-4 mx-auto" />
                  <h3 className="text-xl font-extrabold mb-3">Ready to Connect?</h3>
                  <p className="text-base text-white/90 leading-relaxed mb-6">
                    Fill out our contact form to get started with your renewable energy optimization journey
                  </p>
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-white font-extrabold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base" style={{color: '#1C4D88'}}
                  >
                    <span>Contact Form</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer footer={6} />
    </Layout>
  );
};

export default AboutPage;
